import type { ReactNode } from 'react';
import type { RiskClass } from '@otto-iq/shared';
import { humaniseEnum } from '../lib/format';
import styles from './pill.module.css';

export type PillTone =
  'neutral' | 'brand' | 'accent' | 'info' | 'success' | 'warn' | 'danger' | 'muted';

export interface PillProps {
  tone?: PillTone;
  /** Uppercase micro-label form, for table cells and dense rows. */
  micro?: boolean;
  dot?: boolean;
  /** Pulses the dot — used for `running` and an open live trace. */
  live?: boolean;
  title?: string;
  className?: string;
  children: ReactNode;
}

export function Pill({
  tone = 'neutral',
  micro = false,
  dot = false,
  live = false,
  title,
  className,
  children,
}: PillProps) {
  const classes = [
    styles.pill,
    styles[tone],
    micro ? styles.micro : null,
    live ? styles.live : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} title={title}>
      {dot || live ? <span className={styles.dot} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/** Badge is Pill's alias for non-status labels (counts, categories, channels). */
export const Badge = Pill;

/**
 * One table for every status enum in the system. Keeping it in one place is why
 * `failed` looks the same on a run, an integration and an incident.
 */
const STATUS_TONES: Record<string, PillTone> = {
  // runs
  queued: 'neutral',
  running: 'info',
  awaiting_approval: 'warn',
  done: 'success',
  failed: 'danger',
  stopped: 'muted',
  // approvals
  pending: 'warn',
  approved: 'success',
  rejected: 'danger',
  expired: 'muted',
  // agents / tenants / people
  active: 'success',
  paused: 'warn',
  suspended: 'danger',
  removed: 'muted',
  // integrations
  connected: 'success',
  attention: 'warn',
  available: 'neutral',
  disconnected: 'muted',
  // skills
  meta: 'brand',
  proposed: 'accent',
  locked: 'brand',
  declined: 'muted',
  retired: 'muted',
  // incidents
  open: 'danger',
  acknowledged: 'warn',
  resolved: 'success',
  // releases
  draft: 'neutral',
  staged: 'info',
  live: 'success',
  rolled_back: 'muted',
  // incident severity
  low: 'neutral',
  medium: 'info',
  high: 'warn',
  critical: 'danger',
};

export interface StatusPillProps {
  status: string | null | undefined;
  micro?: boolean;
  label?: string;
  className?: string;
}

export function StatusPill({ status, micro, label, className }: StatusPillProps) {
  const key = (status ?? '').toLowerCase();
  const tone = STATUS_TONES[key] ?? 'neutral';
  const isLive = key === 'running';
  return (
    <Pill tone={tone} micro={micro} dot live={isLive} className={className}>
      {label ?? humaniseEnum(status)}
    </Pill>
  );
}

const RISK_TONES: Record<RiskClass, PillTone> = {
  read: 'info',
  write: 'warn',
  destructive: 'danger',
};

const RISK_LABELS: Record<RiskClass, string> = {
  read: 'Read',
  write: 'Write',
  destructive: 'Destructive',
};

export function RiskPill({
  risk,
  micro = true,
  className,
}: {
  risk: RiskClass;
  micro?: boolean;
  className?: string;
}) {
  return (
    <Pill
      tone={RISK_TONES[risk]}
      micro={micro}
      className={className}
      title={
        risk === 'destructive'
          ? 'Destructive — removes or overwrites data in a connected system.'
          : risk === 'write'
            ? 'Write — changes data in a connected system.'
            : 'Read — looks, never changes anything.'
      }
    >
      {RISK_LABELS[risk]}
    </Pill>
  );
}
